import { Card, Suit } from '../types';
import { getPossibleCaptures } from './gameUtils';

export interface DailyHandChoice {
  id: string;
  label: string;
  cardId: string;
  captureIds: string[];
}

export interface DailyHandScenario {
  id: string;
  title: string;
  prompt: string;
  table: Card[];
  hand: Card[];
  choices: DailyHandChoice[];
  correctChoiceId: string;
  explanation: string;
  lesson: string;
}

const card = (suit: Suit, value: number): Card => ({
  id: `${suit}-${value}`,
  suit,
  value,
});

export const DAILY_HAND_SCENARIOS: DailyHandScenario[] = [
  {
    id: 'protect-settebello',
    title: 'Il Settebello è esposto',
    prompt: 'Quale carta giochi per assicurarti subito il punto più importante?',
    table: [card(Suit.COINS, 7), card(Suit.CUPS, 2), card(Suit.SWORDS, 4)],
    hand: [card(Suit.CLUBS, 7), card(Suit.COINS, 2), card(Suit.SWORDS, 10)],
    choices: [
      { id: 'a', label: '7 di Bastoni: prendi il 7 di Denari', cardId: 'bastoni-7', captureIds: ['denari-7'] },
      { id: 'b', label: '2 di Denari: prendi il 2 di Coppe', cardId: 'denari-2', captureIds: ['coppe-2'] },
      { id: 'c', label: '10 di Spade: lascia la carta sul tavolo', cardId: 'spade-10', captureIds: [] },
    ],
    correctChoiceId: 'a',
    explanation: 'Il 7 di Bastoni ha una presa diretta sul 7 di Denari. Le prese dirette hanno precedenza sulle combinazioni e il Settebello vale un punto certo a fine smazzata.',
    lesson: 'Quando il Settebello è disponibile senza costi strategici, mettilo al sicuro.',
  },
  {
    id: 'avoid-two-coins',
    title: 'Evita il Due di Denari',
    prompt: 'Due prese sono possibili: quale riduce il rischio di un malus pesante?',
    table: [card(Suit.SWORDS, 4), card(Suit.CUPS, 3), card(Suit.COINS, 2)],
    hand: [card(Suit.COINS, 7), card(Suit.CLUBS, 5), card(Suit.CUPS, 9)],
    choices: [
      { id: 'a', label: '7 di Denari: prendi 4 + 3', cardId: 'denari-7', captureIds: ['spade-4', 'coppe-3'] },
      { id: 'b', label: '5 di Bastoni: prendi 3 + 2 di Denari', cardId: 'bastoni-5', captureIds: ['coppe-3', 'denari-2'] },
      { id: 'c', label: '9 di Coppe: prendi 4 + 3 + 2', cardId: 'coppe-9', captureIds: ['spade-4', 'coppe-3', 'denari-2'] },
    ],
    correctChoiceId: 'a',
    explanation: 'La presa con il 7 raccoglie due carte senza acquisire il Due di Denari. Le altre due opzioni includono la carta che assegna -5 punti.',
    lesson: 'Il numero di carte catturate non compensa automaticamente il malus del Due di Denari.',
  },
  {
    id: 'two-card-capture',
    title: 'Più carte, stesso rischio',
    prompt: 'Quale presa raccoglie più carte senza prendere il Due di Denari?',
    table: [card(Suit.COINS, 2), card(Suit.CUPS, 5), card(Suit.SWORDS, 3)],
    hand: [card(Suit.CLUBS, 8), card(Suit.SWORDS, 5), card(Suit.COINS, 6)],
    choices: [
      { id: 'a', label: '8 di Bastoni: prendi 5 + 3', cardId: 'bastoni-8', captureIds: ['coppe-5', 'spade-3'] },
      { id: 'b', label: '5 di Spade: prendi il 5 di Coppe', cardId: 'spade-5', captureIds: ['coppe-5'] },
      { id: 'c', label: '6 di Denari: lascia la carta sul tavolo', cardId: 'denari-6', captureIds: [] },
    ],
    correctChoiceId: 'a',
    explanation: 'L’8 cattura due carte e lascia sul tavolo il Due di Denari. Il 5 è una presa valida ma meno efficiente; il 6 non cattura.',
    lesson: 'A parità di rischio, preferisci la presa che aumenta il controllo delle carte.',
  },
  {
    id: 'make-scopa',
    title: 'Una Scopa immediata',
    prompt: 'Puoi svuotare il tavolo: quale carta devi giocare?',
    table: [card(Suit.SWORDS, 4), card(Suit.CUPS, 3)],
    hand: [card(Suit.CLUBS, 7), card(Suit.COINS, 6), card(Suit.CUPS, 2)],
    choices: [
      { id: 'a', label: '7 di Bastoni: prendi 4 + 3', cardId: 'bastoni-7', captureIds: ['spade-4', 'coppe-3'] },
      { id: 'b', label: '6 di Denari: lascia la carta', cardId: 'denari-6', captureIds: [] },
      { id: 'c', label: '2 di Coppe: lascia la carta', cardId: 'coppe-2', captureIds: [] },
    ],
    correctChoiceId: 'a',
    explanation: 'Il 7 cattura tutte le carte presenti e realizza una Scopa da +1 punto.',
    lesson: 'Prima di scegliere una presa, controlla sempre se puoi svuotare completamente il tavolo.',
  },
  {
    id: 'ace-sweep',
    title: 'Asso piglia tutto',
    prompt: 'Sul tavolo non ci sono Assi. Quale giocata raccoglie tutte le carte?',
    table: [card(Suit.SWORDS, 3), card(Suit.CUPS, 4), card(Suit.COINS, 6)],
    hand: [card(Suit.CLUBS, 1), card(Suit.SWORDS, 7), card(Suit.CUPS, 10)],
    choices: [
      { id: 'a', label: 'Asso di Bastoni: prendi tutto il tavolo', cardId: 'bastoni-1', captureIds: ['spade-3', 'coppe-4', 'denari-6'] },
      { id: 'b', label: '7 di Spade: prendi 3 + 4', cardId: 'spade-7', captureIds: ['spade-3', 'coppe-4'] },
      { id: 'c', label: '10 di Coppe: prendi 4 + 6', cardId: 'coppe-10', captureIds: ['coppe-4', 'denari-6'] },
    ],
    correctChoiceId: 'a',
    explanation: 'Senza un Asso a terra, l’Asso giocato cattura l’intero tavolo. In questa variante lo sweep dell’Asso su più carte non viene però conteggiato come Scopa.',
    lesson: 'Distingui sempre tra “prendere tutto” e realizzare una Scopa valida.',
  },
  {
    id: 'ace-on-table',
    title: 'Asso contro Asso',
    prompt: 'C’è un solo Asso sul tavolo. Qual è la presa corretta?',
    table: [card(Suit.COINS, 1)],
    hand: [card(Suit.CLUBS, 1), card(Suit.SWORDS, 4), card(Suit.CUPS, 7)],
    choices: [
      { id: 'a', label: 'Asso di Bastoni: prendi l’Asso di Denari', cardId: 'bastoni-1', captureIds: ['denari-1'] },
      { id: 'b', label: '4 di Spade: lascia la carta', cardId: 'spade-4', captureIds: [] },
      { id: 'c', label: '7 di Coppe: lascia la carta', cardId: 'coppe-7', captureIds: [] },
    ],
    correctChoiceId: 'a',
    explanation: 'Quando sul tavolo è presente un Asso, l’Asso giocato prende soltanto quell’Asso. Poiché è l’unica carta a terra, la presa vale anche come Scopa.',
    lesson: 'La presenza di un Asso modifica completamente la regola “Asso piglia tutto”.',
  },
  {
    id: 'prevent-zero-coins',
    title: 'Metti al sicuro un Denari',
    prompt: 'Non hai ancora catturato Denari. Quale giocata migliora di più la tua situazione?',
    table: [card(Suit.COINS, 6), card(Suit.SWORDS, 4), card(Suit.CUPS, 2)],
    hand: [card(Suit.CLUBS, 6), card(Suit.COINS, 4), card(Suit.SWORDS, 8)],
    choices: [
      { id: 'a', label: '6 di Bastoni: prendi il 6 di Denari', cardId: 'bastoni-6', captureIds: ['denari-6'] },
      { id: 'b', label: '4 di Denari: prendi il 4 di Spade', cardId: 'denari-4', captureIds: ['spade-4'] },
      { id: 'c', label: '8 di Spade: prendi 6 di Denari + 2', cardId: 'spade-8', captureIds: ['denari-6', 'coppe-2'] },
    ],
    correctChoiceId: 'c',
    explanation: 'Tutte le opzioni possono portare un Denari nella pila catturata, ma l’8 prende due carte e mette al sicuro il 6 di Denari.',
    lesson: 'Evita Zero Denari scegliendo, tra le prese sicure, quella con il miglior rendimento.',
  },
  {
    id: 'choose-settebello-combination',
    title: 'Due combinazioni da 10',
    prompt: 'Il 10 può catturare due coppie diverse. Quale scegli?',
    table: [card(Suit.COINS, 7), card(Suit.SWORDS, 3), card(Suit.CUPS, 6), card(Suit.CLUBS, 4)],
    hand: [card(Suit.CUPS, 10), card(Suit.SWORDS, 7), card(Suit.CLUBS, 6)],
    choices: [
      { id: 'a', label: '10 di Coppe: prendi 7 di Denari + 3', cardId: 'coppe-10', captureIds: ['denari-7', 'spade-3'] },
      { id: 'b', label: '10 di Coppe: prendi 6 + 4', cardId: 'coppe-10', captureIds: ['coppe-6', 'bastoni-4'] },
      { id: 'c', label: '7 di Spade: prendi il 7 di Denari', cardId: 'spade-7', captureIds: ['denari-7'] },
    ],
    correctChoiceId: 'a',
    explanation: 'Entrambe le combinazioni del 10 raccolgono due carte, ma 7 + 3 include il Settebello. È superiore anche alla presa diretta con il 7 perché conserva il 7 di Spade per un turno successivo.',
    lesson: 'Quando più combinazioni hanno lo stesso valore numerico, valuta il valore strategico delle singole carte.',
  },
  {
    id: 'direct-match-priority',
    title: 'La presa diretta ha la precedenza',
    prompt: 'Quale giocata raccoglie due carte senza essere bloccata da una presa diretta?',
    table: [card(Suit.SWORDS, 7), card(Suit.CUPS, 4), card(Suit.CLUBS, 3)],
    hand: [card(Suit.COINS, 7), card(Suit.CUPS, 10), card(Suit.SWORDS, 5)],
    choices: [
      { id: 'a', label: '7 di Denari: prendi il 7 di Spade', cardId: 'denari-7', captureIds: ['spade-7'] },
      { id: 'b', label: '10 di Coppe: prendi 7 + 3', cardId: 'coppe-10', captureIds: ['spade-7', 'bastoni-3'] },
      { id: 'c', label: '5 di Spade: lascia la carta', cardId: 'spade-5', captureIds: [] },
    ],
    correctChoiceId: 'b',
    explanation: 'Giocando un 7 sei obbligato alla presa diretta del 7 a terra e non puoi scegliere 4 + 3. Il 10, invece, può catturare 7 + 3 e raccogliere due carte.',
    lesson: 'Controlla sempre se una presa diretta limita le combinazioni disponibili.',
  },
  {
    id: 'primiera-seven',
    title: 'Costruisci la Primiera',
    prompt: 'Quale presa mette al sicuro la carta con il valore di Primiera più alto?',
    table: [card(Suit.CUPS, 7), card(Suit.COINS, 5), card(Suit.SWORDS, 2)],
    hand: [card(Suit.CLUBS, 7), card(Suit.CUPS, 5), card(Suit.SWORDS, 9)],
    choices: [
      { id: 'a', label: '7 di Bastoni: prendi il 7 di Coppe', cardId: 'bastoni-7', captureIds: ['coppe-7'] },
      { id: 'b', label: '5 di Coppe: prendi il 5 di Denari', cardId: 'coppe-5', captureIds: ['denari-5'] },
      { id: 'c', label: '9 di Spade: prendi 7 + 2', cardId: 'spade-9', captureIds: ['coppe-7', 'spade-2'] },
    ],
    correctChoiceId: 'c',
    explanation: 'Il 9 cattura il 7 di Coppe insieme al 2 di Spade. Mantieni il 7 di Bastoni in mano e acquisisci la carta da 21 punti di Primiera.',
    lesson: 'La Primiera premia soprattutto i 7: quando puoi prenderne uno con una presa efficiente, fallo.',
  },
];

export function getRomeDateKey(date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en', {
    timeZone: 'Europe/Rome',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);

  const values = Object.fromEntries(parts.map(part => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

function hashDateKey(dateKey: string): number {
  let hash = 2166136261;
  for (const char of dateKey) {
    hash ^= char.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

export function getScenarioForDate(dateKey: string): DailyHandScenario {
  return DAILY_HAND_SCENARIOS[hashDateKey(dateKey) % DAILY_HAND_SCENARIOS.length];
}

export function isValidDateKey(value: string | null): value is string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T12:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

function sameCards(actual: Card[], expectedIds: string[]): boolean {
  return actual.map(cardItem => cardItem.id).sort().join('|') === [...expectedIds].sort().join('|');
}

export function isChoiceLegal(scenario: DailyHandScenario, choice: DailyHandChoice): boolean {
  const playedCard = scenario.hand.find(cardItem => cardItem.id === choice.cardId);
  if (!playedCard) return false;

  const possibleCaptures = getPossibleCaptures(playedCard, scenario.table);
  if (choice.captureIds.length === 0) {
    return possibleCaptures.length === 0 || possibleCaptures.some(capture => capture.length === 0);
  }

  return possibleCaptures.some(capture => sameCards(capture, choice.captureIds));
}

export function validateDailyHandScenarios(): string[] {
  const errors: string[] = [];

  for (const scenario of DAILY_HAND_SCENARIOS) {
    const correctChoice = scenario.choices.find(choice => choice.id === scenario.correctChoiceId);
    if (!correctChoice) {
      errors.push(`${scenario.id}: correct choice is missing`);
      continue;
    }

    for (const choice of scenario.choices) {
      if (!isChoiceLegal(scenario, choice)) {
        errors.push(`${scenario.id}: choice ${choice.id} is not legal`);
      }
    }
  }

  return errors;
}

export function cardImagePath(cardItem: Card): string {
  return `/cards_optimized/${cardItem.suit}_${cardItem.value}.webp`;
}
